import { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Grid, Link, Stack, Typography } from '@mui/material';
import { CustomerInfo, NewCreditsOrder, UpdateInfoRequest } from 'orderme-api-integration-client';
import { CustomerClient, OrderClient } from '../../helpers/client';
import { useLocation } from 'react-router';
import { NavBar } from '../appBars/navBar/navBar';
import { RootState } from '../../redux/store';
import { LoginDialog } from '../auth/login/loginDialog';
import { PaymentStatus } from './paymentEnum';
import { assignCustomerDetails } from '../../helpers/customerHelper';
import { PaymentDialog } from './paymentDialog';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/hooks';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { CreditsPaymentsRoute, CreditsRoute } from '../../routes/allRoutes';
import { roundDoubleNumbers } from '../../helpers/currency';
import { CheckoutCard, CheckoutCardHeader } from '../checkout/checkoutStyle';
import { CustomerDetails } from '../checkout/customerDetails';
import { PaymentMethods } from './paymentMethods';
import { StickyCartBar } from '../appBars/cartBar/stickyCartBar';
import { openRules } from '../policy/rulesHelper';

declare let _ApplePaySession: any;

export interface CreditsPaymentParam {
  id: string;
  price: number;
  credits: number;
}

export function CreditsPayments() {
  const routeNavigator = useRouteNavigator();
  const ordersClient: OrderClient = new OrderClient();
  const customersClient: CustomerClient = new CustomerClient();
  const [openLogin, setOpenLogin] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [closeTimeout, setCloseTimeout] = useState<number>(5000);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>();
  const [isPaymentsDisabled, setIsPaymentDisabled] = useState<boolean>(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>();
  const [paymentMustBeSelected, setPaymentMustBeSelected] = useState(false);
  const paymentMethodsRef = useRef<HTMLDivElement>(null);
  const [isRulesApproved, setIsRulesApproved] = useState<boolean>(false);
  const [isCommunicationAgreed, setIsCommunicationAgreed] = useState<boolean>(false);
  const userInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const isUserLoggedIn: boolean = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const paymentTimeoutRef = useRef<number>(0);
  const inAppBrowserRef = useRef<any>(null);
  const exitListenerRef = useRef<(() => void) | null>(null);
  const loadStopListenerRef = useRef<((event: any) => void) | null>(null);

  const { t } = useTranslation();

  const location = useLocation();
  const creditService = location.state as CreditsPaymentParam;

  useEffect(() => {
    if (!isUserLoggedIn) {
      setOpenLogin(true);
      return;
    }

    return () => {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = -1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmCommunication = () => {
    const changedInfo = {
      name: userInfo.name,
      surname: userInfo.surname,
      gender: userInfo.gender,
      birthDate: userInfo.birthDate ? userInfo.birthDate : null,
      communicationAgreed: true,
    } as UpdateInfoRequest;

    customersClient.changeInfo(userInfo.id, changedInfo).catch((ex) => {
      console.error(ex);
    });
  };

  const payForOrder = async (paymentMethodId: string) => {
    setIsPaymentDisabled(true);
    setPaymentStatus(PaymentStatus.Loading);
    setOpenPaymentDialog(true);

    try {
      const creditsOrder = {
        paymentMethodId,
        expectedPrice: creditService.price,
        expectedCredits: creditService.credits,
        serviceId: creditService.id,
      } as NewCreditsOrder;

      const response = await ordersClient.post2(userInfo.id, creditsOrder);
      if (isCommunicationAgreed && isUserLoggedIn) {
        confirmCommunication();
      }
      getOrderInfo(response.id);
    } catch (err) {
      setIsPaymentDisabled(false);
      setPaymentStatus(PaymentStatus.FailedSubmitOrder);
      console.log(err);
    }
  };

  const getOrderInfo = (orderId: string) => {
    ordersClient
      .get2(orderId, userInfo.id)
      .then((response) => {
        if (response.payment?.checkoutUrl) {
          openCheckout(response.payment.checkoutUrl);
          waitForPayment(orderId, response.payment.isActive);
          assignCustomerDetails();
        } else {
          setCloseTimeout(4000);
          setPaymentStatus(PaymentStatus.Success);
          assignCustomerDetails();
        }
      })
      .catch((error) => {
        setIsPaymentDisabled(false);
        setPaymentStatus(PaymentStatus.Failure);
        console.log(error);
      });
  };

  const handlePaymentFailure = () => {
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = 0;
    }
    loadPaymentInfo(PaymentStatus.Failure);
  };

  const openCheckout = (url: string) => {
    if (window.cordova) {
      inAppBrowserRef.current = window.cordova.InAppBrowser.open(url, '_blank', 'location=no');

      exitListenerRef.current = () => handlePaymentFailure();
      loadStopListenerRef.current = (event: any) => {
        if (event.url.toLowerCase().includes('/failure')) {
          handlePaymentFailure();
        }
      };

      inAppBrowserRef.current.addEventListener('exit', exitListenerRef.current);
      inAppBrowserRef.current.addEventListener('loadstop', loadStopListenerRef.current);
    } else {
      if (appStyleMode === AppStyle.Mobile) {
        window.open(url, '_blank');
      } else {
        window.location.replace(url);
      }
    }
  };

  function waitForPayment(orderId: string, isActivated: boolean) {
    let retryCount = 0;
    const MAX_RETRIES = 300;

    async function continueWaiting() {
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = 0;
      }

      if (retryCount >= MAX_RETRIES) {
        loadPaymentInfo(PaymentStatus.Failure);
        return;
      }

      retryCount++;

      try {
        const payment = await ordersClient.getPayment(orderId, userInfo.id);

        if (!isActivated) {
          isActivated = payment.isActive;
          paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
          return;
        }

        if (payment.isActive) {
          paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
          return;
        }

        loadPaymentInfo(payment.paidOn ? PaymentStatus.Success : PaymentStatus.Failure);
      } catch (error) {
        console.error('Error fetching payment info:', error);
        paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
      }
    }

    continueWaiting();
  }

  const loadPaymentInfo = (status: PaymentStatus) => {
    setPaymentStatus(status);
    setOpenPaymentDialog(true);

    if (window.cordova && inAppBrowserRef.current) {
      if (exitListenerRef.current) {
        inAppBrowserRef.current.removeEventListener('exit', exitListenerRef.current);
        exitListenerRef.current = null;
      }
      if (loadStopListenerRef.current) {
        inAppBrowserRef.current.removeEventListener('loadstop', loadStopListenerRef.current);
        loadStopListenerRef.current = null;
      }
      setTimeout(function () {
        inAppBrowserRef.current?.close();
      }, 4000);
    }
  };

  const closePaymentDialog = () => {
    if (paymentStatus === PaymentStatus.Failure) {
      routeNavigator.goHome(true);
      routeNavigator.goToOrderHistory();
    } else if (paymentStatus === PaymentStatus.Success) {
      assignCustomerDetails();
      setTimeout(() => routeNavigator.goHome(true), 500);
    } else {
      setOpenPaymentDialog(false);
    }
  };

  const pay = () => {
    if (!selectedPaymentId) {
      setPaymentMustBeSelected(true);
      paymentMethodsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    payForOrder(selectedPaymentId);
  };

  const closeLogin = () => {
    setOpenLogin(false);
    if (!isUserLoggedIn) {
      routeNavigator.goBack();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NavBar barTitle={t('payment:paymentMethods')} />
      <BreadcrumbsBar paths={[{ path: CreditsRoute, isLink: true }, { path: CreditsPaymentsRoute }]} />
      <Grid
        sx={{
          width: '100%',
          backgroundColor: 'white',
          color: 'black',
          borderRadius: '15px',
          p: '10px',
        }}
        className={appStyleMode === AppStyle.Mobile ? 'checkout-container' : undefined}
      >
        <Typography fontWeight="bold" variant="h6" sx={{ mb: '10px', ml: '10px' }}>
          {t('payment:orderCheckout')}
        </Typography>
        <Grid container spacing={{ xs: 0, sm: 1, md: 2 }}>
          <Grid ref={paymentMethodsRef} sx={{ scrollMargin: '200px' }} size={{ xs: 12, sm: 'grow' }}>
            <CheckoutCard
              sx={{
                borderColor: paymentMustBeSelected && 'error.main',
                borderWidth: paymentMustBeSelected && 2,
                borderStyle: paymentMustBeSelected && 'solid',
              }}
            >
              <CheckoutCardHeader>{t('payment:paymentMethods')}</CheckoutCardHeader>
              <PaymentMethods
                isDisabled={isPaymentsDisabled}
                onPaymentSelected={(id) => {
                  setSelectedPaymentId(id);
                  setPaymentMustBeSelected(false);
                }}
              />
            </CheckoutCard>
          </Grid>

          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Box sx={{ width: { sm: 280, md: 420, lg: 560, xl: 620 } }}>
              <CheckoutCard>
                <CheckoutCardHeader>{t('payment:customerInfo')}</CheckoutCardHeader>
                <CustomerDetails />
              </CheckoutCard>

              <CheckoutCard sx={{ backgroundColor: '#f5f5f5', px: '10px' }}>
                <CheckoutCardHeader>{t('payment:orderSummary')}</CheckoutCardHeader>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('payment:creditsAmount')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {roundDoubleNumbers(creditService.credits)} €
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: '4px' }}>
                  <Typography variant="body2">{t('payment:price')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {roundDoubleNumbers(creditService.price)} €
                  </Typography>
                </Stack>
              </CheckoutCard>

              <FormControlLabel
                sx={{ ml: '0px', mb: '10px' }}
                control={
                  <Checkbox
                    sx={{ '& .MuiSvgIcon-root': { fontSize: '20px', color: 'secondary.main' } }}
                    checked={isRulesApproved}
                    onChange={() => setIsRulesApproved(!isRulesApproved)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" display="inline">
                      {t('payment:agreeECommerceRulesPart1')}
                    </Typography>
                    <Typography variant="body2" display="inline">
                      <Link
                        component="button"
                        underline="hover"
                        sx={{ verticalAlign: 'baseline' }}
                        onClick={() => openRules(routeNavigator.getRulesPath())}
                      >
                        {t('payment:agreeECommerceRulesPart2')}
                      </Link>
                    </Typography>
                  </Box>
                }
              />
              {isUserLoggedIn && (
                <FormControlLabel
                  sx={{ ml: '0px', mb: '10px' }}
                  control={
                    <Checkbox
                      sx={{ '& .MuiSvgIcon-root': { fontSize: '20px', color: 'secondary.main' } }}
                      checked={isCommunicationAgreed}
                      onChange={() => setIsCommunicationAgreed(!isCommunicationAgreed)}
                    />
                  }
                  label={<Typography variant="body2">{t('payment:agreeCommunication')}</Typography>}
                />
              )}

              <Box>
                <StickyCartBar
                  open={pay}
                  barText={t('payment:pay')}
                  isDisabled={isPaymentsDisabled || !isRulesApproved}
                  isPurchase={false}
                  price={creditService.price}
                  hideBadge
                  sx={{ mb: '20px' }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <LoginDialog open={openLogin} onClose={() => closeLogin()} />
      <PaymentDialog
        open={openPaymentDialog}
        onClose={closePaymentDialog}
        paymentStatus={paymentStatus}
        autoCloseDelay={closeTimeout}
      />
    </Box>
  );
}
